import { AxiosError, AxiosRequestConfig } from 'axios'
import { useSnackbar } from 'notistack'
import {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useCallback,
  useRef
} from 'react'

// utils
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth'
import { isValidToken } from '../utils/jwt'

import { HTTP_STATUS } from 'src/constants/http.constants'
import Api from 'src/services/Api'
import LocalStorage from 'src/services/LocalStorage'

// Avoid multiple concurrent refresh calls across the app
let isRefreshing = false
let refreshPromise: Promise<unknown> | null = null

/** Token shape from auth API (may be under .data or at top level) */
interface AuthTokenPayload {
  access_token?: string
  refresh_token?: string
  device_id?: string | null
}

/** Auth API response: either { data: tokens } or tokens at top level */
type AuthApiResponse = { data?: AuthTokenPayload } & Partial<AuthTokenPayload>

/** Extended request config for retry-once during token refresh */
interface RequestConfigWithRetry extends AxiosRequestConfig {
  retry?: boolean
}

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER'
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean
    user: AuthUser
  }
  [Types.Login]: {
    user: AuthUser
  }
  [Types.Logout]: undefined
}

export type JWTActions =
  ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>]

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
}

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user
      }

    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: action.payload.user?.login,
        user: action.payload.user
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null
      }

    default:
      return state
  }
}

const AuthContext = createContext<JWTContextType | null>(null)

type AuthProviderProps = {
  children: ReactNode
}

const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState)
  const { enqueueSnackbar } = useSnackbar()
  const sessionCheckRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearSession = useCallback(() => {
    LocalStorage.removeItem('accessToken')
    LocalStorage.removeItem('refreshToken')
    Api.setAuthToken(null)

    dispatch({ type: Types.Logout })
  }, [])

  const refresh = useCallback(
    async (currentRefreshToken?: string) => {
      const storedRefreshToken =
        currentRefreshToken ||
        (LocalStorage.getItem('refreshToken') as string | null)

      if (!storedRefreshToken || !isValidToken(storedRefreshToken)) {
        clearSession()
        throw new Error('Invalid refresh token')
      }

      try {
        const response = await Api.refreshToken({
          refreshToken: storedRefreshToken
        })

        const res = response as AuthApiResponse
        const tokens: AuthTokenPayload = res.data ?? res

        const { access_token, refresh_token, device_id } = tokens || {}

        if (!access_token || !refresh_token) {
          clearSession()
          throw new Error('Missing tokens in refresh response')
        }

        Api.setAuthToken(access_token)
        LocalStorage.setItem('accessToken', access_token)
        LocalStorage.setItem('refreshToken', refresh_token)
        if (device_id) LocalStorage.setItem('deviceId', device_id)

        const me = await Api.getMe()

        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: me?.data?.login,
            user: me?.data
          }
        })
      } catch (error) {
        console.error('Refresh token request failed', error)
        clearSession()
        throw error
      }
    },
    [clearSession]
  )

  const handleUnauthorizedResponse = useCallback(
    async (error: AxiosError): Promise<unknown> => {
      const status = error.response?.status
      const originalRequest = (error.config || {}) as RequestConfigWithRetry

      const refreshToken = LocalStorage.getItem('refreshToken') as string | null

      // Never try to refresh again if the refresh endpoint itself failed
      if (
        originalRequest.url &&
        originalRequest.url.includes('/auth/refresh')
      ) {
        clearSession()
        return Promise.reject(error)
      }

      if (status === HTTP_STATUS.UNAUTHORIZED) {
        if (!refreshToken || !isValidToken(refreshToken)) {
          clearSession()
          return Promise.reject(error)
        }

        if (originalRequest.retry) {
          clearSession()
          return Promise.reject(error)
        }

        originalRequest.retry = true

        try {
          if (!isRefreshing) {
            isRefreshing = true
            refreshPromise = refresh(refreshToken).finally(() => {
              isRefreshing = false
              refreshPromise = null
            })
          }

          if (refreshPromise) {
            await refreshPromise
          }

          const newAccessToken = LocalStorage.getItem('accessToken') as
            | string
            | null

          if (!newAccessToken) {
            clearSession()
            return await Promise.reject(error)
          }

          Api.setAuthToken(newAccessToken)

          const method = (originalRequest.method || 'get').toLowerCase() as
            | 'get'
            | 'post'
            | 'put'
            | 'patch'
            | 'delete'

          let requestUrl = originalRequest.url || ''

          if (
            originalRequest.baseURL &&
            requestUrl.startsWith(originalRequest.baseURL)
          ) {
            requestUrl = requestUrl.slice(originalRequest.baseURL.length)
          }

          const retryConfig = {
            ...originalRequest,
            headers: {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newAccessToken}`
            }
          }

          let retryResult: unknown

          switch (method) {
            case 'get':
              retryResult = await Api.get(requestUrl, retryConfig)
              break
            case 'post':
              retryResult = await Api.post(requestUrl, {
                ...retryConfig,
                data: originalRequest.data
              })
              break
            case 'put':
              retryResult = await Api.put(requestUrl, {
                ...retryConfig,
                data: originalRequest.data
              })
              break
            case 'patch':
              retryResult = await Api.patch(requestUrl, {
                ...retryConfig,
                data: originalRequest.data
              })
              break
            case 'delete':
            default:
              retryResult = await Api.del(requestUrl, retryConfig)
              break
          }

          // Wrap so the original makeRequest .then(resp => resp.data)
          // unwraps correctly instead of double-unwrapping.
          return { data: retryResult }
        } catch (refreshError) {
          clearSession()
          return Promise.reject(refreshError)
        }
      }

      if (status === HTTP_STATUS.FORBIDDEN) {
        enqueueSnackbar("You don't have permission to perform this action", {
          variant: 'error'
        })
        return Promise.reject(error)
      }

      return Promise.reject(error)
    },
    [clearSession, refresh, enqueueSnackbar]
  )

  const validateSession = useCallback(async () => {
    const accessToken = LocalStorage.getItem('accessToken') as string | null

    if (!accessToken || !isValidToken(accessToken)) {
      return
    }

    try {
      await Api.getMe()
    } catch {
      // 401 interceptor handles clearSession automatically
    }
  }, [])

  useEffect(() => {
    Api.appendResponseErrorInterceptor(handleUnauthorizedResponse)

    const initialize = async () => {
      try {
        const accessToken = LocalStorage.getItem('accessToken') as string | null

        if (accessToken && isValidToken(accessToken)) {
          Api.setAuthToken(accessToken)

          const me = await Api.getMe()

          return dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: me?.data?.login,
              user: me?.data
            }
          })
        }

        return dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null
          }
        })
      } catch (err) {
        console.error(err)
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null
          }
        })
      }
    }

    initialize()

    // Tab sync: respond to token changes from other tabs
    const handleStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== localStorage ||
        (event.key !== 'accessToken' && event.key !== 'refreshToken')
      ) {
        return
      }

      const accessToken = LocalStorage.getItem('accessToken') as string | null

      if (accessToken && isValidToken(accessToken)) {
        initialize()
      } else {
        clearSession()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession()
      }
    }

    window.addEventListener('storage', handleStorage)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    sessionCheckRef.current = setInterval(
      validateSession,
      SESSION_CHECK_INTERVAL_MS
    )

    return () => {
      window.removeEventListener('storage', handleStorage)
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current)
      }
    }
  }, [clearSession, handleUnauthorizedResponse, validateSession])

  const login = async (userName: string, password: string) => {
    const response = await Api.login(userName, password)

    const res = response as AuthApiResponse
    const tokens: AuthTokenPayload = res.data ?? res

    const { access_token, refresh_token, device_id } = tokens || {}

    if (!access_token || !refresh_token) {
      throw new Error('Missing tokens in login response')
    }

    LocalStorage.setItem('accessToken', access_token)
    LocalStorage.setItem('refreshToken', refresh_token)
    if (device_id) LocalStorage.setItem('deviceId', device_id)
    Api.setAuthToken(access_token)

    const me = await Api.getMe()

    dispatch({
      type: Types.Login,
      payload: {
        user: me.data
      }
    })
  }

  const logout = async (userId: string) => {
    try {
      const refreshToken = LocalStorage.getItem('refreshToken') as string | null

      if (userId && refreshToken) {
        await Api.logout(userId, refreshToken)
      }
    } catch (error) {
      console.error('Logout request failed', error)
    } finally {
      clearSession()
    }
  }

  const logoutFromAllDevices = async () => {
    try {
      await Api.revokeAllSessions()
    } catch (error) {
      console.error('Logout from all devices failed', error)
    } finally {
      clearSession()
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        logoutFromAllDevices,
        refresh
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
